import { ColumnDef } from "@tanstack/react-table";
import { Avatar } from "@mui/material";
import {
	enumToStringConverter,
	moneyFormat,
} from "../../../helpers/appHelpers";

// Type definitions
interface PayPeriod {
	code: string;
}

interface Employee {
	firstName: string;
	lastName: string;
	photoUrl?: string;
}

interface ExplicitDeductionRow {
	id: number;
	name: string;
	code: string;
	description: string;
	applyOn: string;
	fixedComponent: number;
	rateComponent: number;
	startPayPeriod?: PayPeriod;
	endPayPeriod?: PayPeriod;
	createdAt?: string;
	modifiedAt?: string;
}

interface LoanRow {
	id: number;
	employee?: Employee;
	amount: number;
	description: string;
	repaymentType: string;
	interestRate: number;
	numberOfMonths: number;
	startPayPeriod?: PayPeriod;
	repaymentAmount: number;
	initialPayment: number;
	finalPayment: number;
	createdAt?: string;
	modifiedAt?: string;
}

// ========== Explicit Deductions Columns ==========
export const explicitDeductionsColumns: ColumnDef<ExplicitDeductionRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 380,
	},
	{
		id: "applyOn",
		accessorKey: "applyOn",
		header: "Apply On",
		size: 180,
		cell: ({ row }) => enumToStringConverter(row.original.applyOn),
	},
	{
		id: "fixedComponent",
		accessorKey: "fixedComponent",
		header: "Fixed Component",
		size: 220,
		cell: ({ row }) => moneyFormat(row.original.fixedComponent),
	},
	{
		id: "rateComponent",
		accessorKey: "rateComponent",
		header: "Rate Component",
		size: 220,
		cell: ({ row }) =>
			(Number(row.original.rateComponent) * 100).toFixed(2) + "%",
	},
	{
		id: "startPayPeriod",
		accessorKey: "startPayPeriod",
		header: "Start Pay Period",
		size: 220,
		cell: ({ row }) => row.original.startPayPeriod?.code || "--",
	},
	{
		id: "endPayPeriod",
		accessorKey: "endPayPeriod",
		header: "End Pay Period",
		size: 220,
		cell: ({ row }) => row.original.endPayPeriod?.code || "--",
	},
];

export const explicitDeductionsColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	applyOn: true,
	fixedComponent: true,
	rateComponent: true,
	startPayPeriod: false,
	endPayPeriod: false,
};

// ========== Loans Columns ==========
export const loansColumns: ColumnDef<LoanRow>[] = [
	{
		id: "employee",
		accessorKey: "employee",
		header: "Employee",
		size: 300,
		cell: ({ row }) => {
			const employee = row.original.employee;
			if (!employee) return null;

			return (
				<div className="flex items-center space-x-4">
					<Avatar
						src={employee.photoUrl || ""}
						alt="employee"
						className="w-10 h-10 rounded-full"
					/>
					<p className="text-sm truncate">
						{employee.firstName + " " + employee.lastName}
					</p>
				</div>
			);
		},
	},
	{
		id: "amount",
		accessorKey: "amount",
		header: "Amount",
		size: 200,
		cell: ({ row }) => moneyFormat(row.original.amount),
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "repaymentType",
		accessorKey: "repaymentType",
		header: "Repayment Type",
		size: 180,
		cell: ({ row }) => enumToStringConverter(row.original.repaymentType),
	},
	{
		id: "interestRate",
		accessorKey: "interestRate",
		header: "Interest Rate",
		size: 180,
		cell: ({ row }) =>
			(Number(row.original.interestRate) * 100).toFixed(2) + "%",
	},
	{
		id: "numberOfMonths",
		accessorKey: "numberOfMonths",
		header: "Number Of Months",
		size: 200,
	},
	{
		id: "startPayPeriod",
		accessorKey: "startPayPeriod",
		header: "Start Pay Period",
		size: 220,
		cell: ({ row }) => row.original.startPayPeriod?.code || "--",
	},
	{
		id: "repaymentAmount",
		accessorKey: "repaymentAmount",
		header: "Repayment Amount",
		size: 200,
		cell: ({ row }) => moneyFormat(row.original.repaymentAmount),
	},
	{
		id: "initialPayment",
		accessorKey: "initialPayment",
		header: "Initial Payment",
		size: 200,
		cell: ({ row }) => moneyFormat(row.original.initialPayment),
	},
	{
		id: "finalPayment",
		accessorKey: "finalPayment",
		header: "Final Payment",
		size: 200,
		cell: ({ row }) => moneyFormat(row.original.finalPayment),
	},
];

export const loansColumnVisibility: Record<string, boolean> = {
	employee: true,
	amount: true,
	description: true,
	repaymentType: true,
	interestRate: true,
	numberOfMonths: true,
	startPayPeriod: true,
	repaymentAmount: false,
	initialPayment: false,
	finalPayment: false,
};
